<template>
  <div>
    <ValidationObserver ref="generalInformationForm">
      <div class="d-flex">
        <SectionTitle
          has-line
          content="Información General"
          :class="isMobile ? 'section-title' : ''"
        />
      </div>

      <div class="row d-flex justify-content-start">
        <div class="col-md-4 col-12">
          <ValidationProvider v-slot="{ errors }" rules="required">
            <custom-drop-down
              v-if="userCanEdit"
              id="ddlSchoolType"
              v-model="form.schoolType"
              :options="schoolTypes"
              label="Tipo de Escuela:"
              :regular-label="isMobile"
              fieldkey="id"
              fieldname="name"
              placeholder="Seleccione"
              class="height-fixed"
              :disabled="isEditMode"
              :error="errors[0]"
              :custom-error-msg="getError('24001')"
            />
            <custom-input-display
              v-else
              v-model="form.schoolType.name"
              :label="'Tipo de Escuela:'"
            />
          </ValidationProvider>
        </div>

        <div class="col-md-4 col-12">
          <ValidationProvider
            v-slot="{ errors }"
            :custom-messages="{
              completePhone: 'Se deben completar los números telefónicos.',
              required: 'El campo teléfono es obligatorio',
            }"
            name="teléfono2"
            rules="required|completePhone"
          >
            <custom-input
              id="txtPhone"
              v-model="form.phone"
              label="Número Telefónico:"
              :label-class="isMobile ? 'text-title' : ''"
              :mask-type="'Phone'"
              placeholder="(000) 000-0000"
              :error="errors[0]"
              :custom-error-msg.sync="errors[0]"
              :disabled="!userCanEdit"
            />
          </ValidationProvider>
        </div>

        <div v-if="showSchoolCode" class="col-md-4 col-12">
          <ValidationProvider v-slot="{ errors }" rules="required">
            <custom-input
              id="txtSieSchoolCode"
              v-model="form.sieSchoolCode"
              maxlength="5"
              mask-type="Zipcode"
              placeholder="00000"
              label="Código de Escuela:"
              :label-class="isMobile ? 'text-title' : ''"
              :class="readOnly"
              :error="errors[0]"
              :custom-error-msg="getError('24002')"
              :disabled="!userCanEdit"
            />
          </ValidationProvider>
        </div>

        <div :class="['col-12 col-md-4', { 'mt-3': isMobile }]">
          <ValidationProvider v-slot="{ errors }" rules="required">
            <custom-input
              id="txtLocationName"
              v-model="form.locationName"
              maxlength="500"
              label="Nombre de Escuela:"
              :label-class="isMobile ? 'text-title' : ''"
              :error="errors[0]"
              :custom-error-msg="getError('24003')"
              :disabled="!userCanEdit"
            />
          </ValidationProvider>
        </div>

        <div :class="['col-md-4 col-12', { 'mt-3': isMobile }]">
          <ValidationProvider v-slot="{ errors }" rules="required">
            <custom-input
              id="txtPhysicalAddressLine1"
              v-model="form.physicalAddressLine1"
              label="Dirección Línea 1:"
              :label-class="isMobile ? 'text-title' : ''"
              :error="errors[0]"
              :custom-error-msg="getError('24005')"
              :disabled="!userCanEdit"
            />
          </ValidationProvider>
        </div>

        <div :class="['col-md-4 col-12', { 'mt-3': isMobile }]">
          <custom-input
            id="txtPhysicalAddressLine2"
            v-model="form.physicalAddressLine2"
            label="Dirección Línea 2:"
            :label-class="isMobile ? 'text-title' : ''"
            :disabled="!userCanEdit"
          />
        </div>
        <div class="col-md-4 col-12">
          <ValidationProvider v-slot="{ errors }" rules="required">
            <custom-drop-down
              v-if="userCanEdit"
              id="ddlPhysicalMunicipality"
              v-model="form.physicalMunicipality"
              :options="municipalities"
              label="Municipio:"
              :regular-label="isMobile"
              fieldkey="id"
              fieldname="name"
              placeholder="Seleccione"
              class="height-fixed"
              :error="errors[0]"
              :custom-error-msg="getError('24006')"
              :disabled="!userCanEdit"
            />
            <custom-input-display
              v-else
              v-model="form.physicalMunicipality.name"
              :label="'Municipio:'"
            />
          </ValidationProvider>
        </div>
      </div>

      <div
        :class="[
          'row d-flex justify-content-start',
          { 'mt-4': !isMobile, 'mt-3': isMobile },
        ]"
      >
        <div :class="['col-md-4 col-12', { 'mt-3': isMobile }]">
          <custom-drop-down
            v-if="userCanEdit"
            id="ddlPhysicalState"
            v-model="form.physi"
            :options="states"
            fieldkey="id"
            fieldname="name"
            label="Estado:"
            :regular-label="isMobile"
            placeholder="PR"
            :disabled="true"
          />
          <custom-input-display v-else :label="'Estado:'" value="PR" />
        </div>

        <div :class="['col-md-4 col-12', { ' mt-3': isMobile }]">
          <ValidationProvider
            v-slot="{ errors }"
            :custom-messages="{
              min: 'El código postal no es válido.',
              max: 'El código postal no es válido.',
              required: 'El código postal es requerido.',
            }"
            rules="required|min:5|max:10"
          >
            <custom-input
              v-if="userCanEdit"
              id="txtPhysicalZipCode"
              v-model="form.physicalZipCode"
              :masked="true"
              mask-type="ZipcodeExt"
              placeholder="00000-0000"
              label="Código Postal:"
              :label-class="isMobile ? 'text-title' : ''"
              :error="errors[0]"
              :custom-error-msg.sync="errors[0]"
              :disabled="!userCanEdit"
            />
            <custom-input-display
              v-else
              v-model="form.physicalZipCode"
              :label="'Código Postal:'"
            />
          </ValidationProvider>
        </div>
        <div class="col-md-2 col-12">
          <custom-input
            id="txtLatitude"
            v-model="form.latitude"
            max-length="11"
            label="Latitud:"
            :label-class="isMobile ? 'text-title' : ''"
            :masked="true"
            mask-type="Coords"
            placeholder="00.00000000"
            :disabled="!userCanEdit"
          />
        </div>

        <div :class="['col-md-2 col-12', { 'mt-3': isMobile }]">
          <custom-input
            id="txtLongitude"
            v-model="form.longitude"
            max-length="11"
            label="Longitud:"
            :label-class="isMobile ? 'text-title' : ''"
            :masked="true"
            mask-type="Coords"
            placeholder="00.00000000"
            :disabled="!userCanEdit"
          />
        </div>
      </div>

      <div
        :class="[
          'row d-flex justify-content-start',
          { 'mt-4': !isMobile, 'mt-3': isMobile },
        ]"
      >
        <div v-if="isInstitution" class="col-md-4 col-12">
          <ValidationProvider v-slot="{ errors }" rules="">
            <custom-drop-down
              id="ddlProvider"
              v-model="form.provider"
              :options="providers"
              label="Proveedor:"
              :regular-label="isMobile"
              fieldkey="id"
              fieldname="name"
              placeholder="Seleccione"
              class="height-fixed"
              :disabled="!userCanEdit"
              :error="errors[0]"
              :custom-error-msg="getError('0253')"
            />
          </ValidationProvider>
        </div>
        <div :class="['col-md-4 col-12', { 'mt-3': isMobile }]">
          <custom-input
            id="txtPrincipalFirstName"
            v-model="form.principalFirstName"
            label="Nombre del Director:"
            :label-class="isMobile ? 'text-title' : ''"
            :disabled="!userCanEdit"
          />
        </div>

        <div :class="['col-md-4 col-12', { 'mt-3': isMobile }]">
          <custom-input
            id="txtPrincipalLastName"
            v-model="form.principalLastName"
            label="Apellido del Director:"
            :label-class="isMobile ? 'text-title' : ''"
            :disabled="!userCanEdit"
          />
        </div>
        <div class="col-md-4 col-12">
          <ValidationProvider v-slot="{ errors }" rules="">
            <custom-drop-down
              v-if="userCanEdit"
              id="rlvId"
              v-model="form.rlvType"
              :options="rlvTypes"
              label="RLV:"
              :regular-label="isMobile"
              fieldkey="id"
              fieldname="name"
              placeholder="Seleccione"
              class="height-fixed"
              :disabled="!userCanEdit"
              :error="errors[0]"
            />
            <custom-input-display
              v-else
              v-model="form.rlvType.name"
              :label="'RLV:'"
            />
          </ValidationProvider>
        </div>

        <div class="d-flex pt-1">
          <div
            v-if="medicaidEnabled"
            :class="['pl-3', { 'mt-3': isMobile }, { 'pl-4': isMobile }]"
          >
            <div v-if="hasId" class="custom-control custom-checkbox mt-3">
              <input
                id="medicaidFlag"
                v-model="form.medicaidFlag"
                type="checkbox"
                class="custom-control-input"
                :checked="form.medicaidFlag"
                :disabled="!userCanEdit"
              />
              <label class="custom-control-label" for="medicaidFlag">
                La escuela pertenece al proyecto Medicaid
              </label>
            </div>
          </div>
          <div :class="['pl-3', { 'mt-3': isMobile }, { 'pl-4': isMobile }]">
            <div class="custom-control custom-checkbox mt-3">
              <input
                id="casaFlag"
                v-model="form.casaFlag"
                type="checkbox"
                class="custom-control-input"
                :checked="form.casaFlag"
                :disabled="!userCanEdit"
              />
              <label class="custom-control-label" for="casaFlag">
                La escuela pertenece al proyecto C.A.S.A.
              </label>
            </div>
          </div>
          <div :class="['pl-3', { 'mt-3': isMobile }]">
            <div class="custom-control custom-checkbox mt-3">
              <input
                id="idearFlag"
                v-model="form.idearFlag"
                type="checkbox"
                class="custom-control-input"
                :checked="form.idearFlag"
                :disabled="!userCanEdit"
              />
              <label class="custom-control-label" for="idearFlag">
                IDEAR
              </label>
            </div>
          </div>
          <div :class="['pl-3', { 'mt-3': isMobile }, { 'pl-4': isMobile }]">
            <div class="custom-control custom-checkbox mt-3">
              <input
                id="isValidForAee"
                v-model="form.isValidForAee"
                type="checkbox"
                class="custom-control-input"
                :checked="form.isValidForAee"
                :disabled="!userCanEdit"
              />
              <label class="custom-control-label" for="isValidForAee">
                La escuela es válida para AEE
              </label>
            </div>
          </div>
        </div>
      </div>
    </ValidationObserver>
  </div>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import SectionTitle from 'SectionTitle';
import CustomDropDown from 'custom-drop-down';
import CustomInput from 'CustomInput';
import CustomInputDisplay from 'CustomInputDisplay';
import { modulesName } from '@/modules';
import { createNamespacedHelpers } from 'vuex';
import {
  getterTypes,
  actionTypes,
} from '@/modules/operation-school-management/store/types';
const { mapGetters, mapActions } = createNamespacedHelpers(
  modulesName.operationSchoolManagementModuleName
);
import SCHOOL_TYPES from '@/utils/constants/student/schoolTypes.js';
import resourcesService from '@/services/resources.js';
import {
  getProvidersByAcademicyearId,
  getProvidersForDirectPayment,
} from '@/modules/compensatory-service/api.js';

export default {
  name: 'SchoolInformation',
  components: {
    ValidationObserver,
    ValidationProvider,
    SectionTitle,
    CustomDropDown,
    CustomInput,
    CustomInputDisplay,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    medicaidEnabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      resources: [],
      states: [{ id: 0, name: 'PR' }],
      providers: [],
    };
  },
  computed: {
    ...mapGetters({
      schoolTypes: getterTypes.GET_SCHOOL_TYPES,
      municipalities: getterTypes.GET_MUNICIPALITIES,
      rlvTypes: getterTypes.GET_RLVTYPES,
    }),
    hasId() {
      return !!this.$route.params.id;
    },
    showSchoolCode() {
      return (
        this.form.schoolType &&
        this.form.schoolType.id === SCHOOL_TYPES.PublicSchool
      );
    },
    isInstitution() {
      return (
        this.form.schoolType &&
        this.form.schoolType.id == SCHOOL_TYPES.Institutions
      );
    },
    isEditMode() {
      return this.$route.params.id ? true : false;
    },
    userCanEdit() {
      return this.$store.getters.HasPermission('school.edit');
    },
    readOnly() {
      return this.isEditMode ? 'read-only' : '';
    },
    isMobile() {
      return this.$store.getters.getIsMobileDensity;
    },
    form: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
  },
  methods: {
    ...mapActions([
      actionTypes.FETCH_SCHOOL_TYPES,
      actionTypes.FETCH_MUNICIPALITIES,
      actionTypes.FETCH_SCHOOL_BY_ID,
      actionTypes.FETCH_RLVTYPES,
    ]),
    getError(code) {
      let message = this.resources[code];

      return message ? message.replace(`(${code})`, '') : '';
    },
    async loadResources() {
      this.resources = await resourcesService.LoadValidationMessages([
        '24001',
        '24002',
        '24003',
        '24004',
        '24005',
        '24006',
        '24007',
        '0253',
      ]);
    },
    async getSchool(id) {
      const school = await this[actionTypes.FETCH_SCHOOL_BY_ID]({ id });
      if (school) {
        this.form = {
          ...school,
          idearFlag: school.idearFlag == '1',
          casaFlag: school.casaFlag == '1',
          schoolType: this.schoolTypes.find((x) => x.id == school.schoolType),

          physicalMunicipality: this.municipalities.find(
            (x) => x.name == school.physicalMunicipality
          ),
          rlvType: this.rlvTypes.find(
            (x) => x.id == school.rlvClassificationTypeId
          ),
          physicalZipCode: [school.physicalZipCode, school.physicalZipCodePlus]
            .filter((x) => x)
            .join('-'),
          physicalState: { id: 0, name: 'PR' },
        };
        await this.loadCurrentYear();
      }
    },
    async loadCurrentYear() {
      this.$ApiGet(`api/academicyears/current`).then((response) => {
        if (!this.isInstitution) {
          getProvidersByAcademicyearId(response.data.id).then((response) => {
            this.providers = response.data;
            this.form.provider = this.providers.find(
              (x) => x.id == this.form.providerId
            );
          });
        } else {
          getProvidersForDirectPayment(response.data.id).then((response) => {
            this.providers = response.data;
            this.form.provider = this.providers.find(
              (x) => x.id == this.form.providerId
            );
          });
        }
      });
    },
  },
  async mounted() {
    await this[actionTypes.FETCH_SCHOOL_TYPES]();
    await this[actionTypes.FETCH_MUNICIPALITIES]();
    await this[actionTypes.FETCH_RLVTYPES]();
    await this.loadResources();

    if (this.isEditMode) {
      this.getSchool(this.$route.params.id);
    }
  },
};
</script>

<style scoped>
.read-only >>> input {
  pointer-events: none;
  cursor: not-allowed !important;
  background-color: #f8f8f8;
}

.height-fixed >>> .vs__dropdown-toggle {
  height: calc(1.5em + 0.75rem + 2px) !important;
}

::v-deep .text-title {
  font-size: 12px !important;
  font-weight: normal !important;
}

@media only screen and (max-width: 375px) {
  ::v-deep .regular {
    font-size: 12px !important;
    font-weight: normal !important;
  }

  ::v-deep .tag-badge-title-info {
    font-size: 12px !important;
  }

  .section-title {
    width: 100% !important;
  }

  ::v-deep .input-display {
    font-size: 12px;
  }

  ::v-deep .input-display > label {
    color: #727272 !important;
    font-weight: normal !important;
  }
}
</style>
